import type { BoltConfigurationResult } from '../../../shared/services/boltConfigurationService';
import {
  boltConnectionTooltips,
  boltOptionTooltips,
} from '../../../shared/config/wizardTooltips';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

export interface Notification {
  open: boolean;
  severity: NotificationSeverity;
  title: string;
  message: string;
}

export type BoltFieldKey =
  | 'boltSetActiveValue'
  | 'boltItemDescriptionValue'
  | 'boltDiaValue'
  | 'boltQuantityValue'
  | 'boltLengthValue'
  | 'boltMaterialValue'
  | 'boltStandardValue'
  | 'boltBaseFamilyValue'
  | 'boltModelingChoice';

export interface BoltFieldDefinition {
  field: BoltFieldKey;
  label: string;
  help: (typeof boltConnectionTooltips)[keyof typeof boltConnectionTooltips];
  required: boolean;
  readOnly?: boolean;
  focusTokens: string[];
}

export const BOLT_BASE_FAMILY_OPTIONS = [
  {
    value: 'BoltBased',
    label: 'BoltBased',
    tooltip: boltOptionTooltips.baseFamilyBoltBased,
    description: 'Uses bolt-based source BOLT data without deriving BOLT elements from GASKET records.',
  },
  {
    value: 'GasketBased',
    label: 'GasketBased',
    tooltip: boltOptionTooltips.baseFamilyGasketBased,
    description: 'Derives standalone BOLT elements from qualifying GASKET records.',
  },
];

export const BOLT_MODELING_OPTIONS = [
  {
    value: 'no',
    label: 'Derive BOLT from GASKET',
    tooltip: boltOptionTooltips.modelGasketBased,
    description: 'Derives standalone BOLT elements from qualifying GASKET records.',
  },
  {
    value: 'yes',
    label: 'Use source BOLT data',
    tooltip: boltOptionTooltips.modelBoltBased,
    description: 'Uses source BOLT data as-is without deriving BOLT elements from GASKET records.',
  },
];

export const CORE_FIELD_DEFINITIONS: readonly BoltFieldDefinition[] = [
  {
    field: 'boltDiaValue',
    label: 'BOLT-DIA *',
    help: boltConnectionTooltips.boltDia,
    required: true,
    focusTokens: ['BOLT-DIA'],
  },
  {
    field: 'boltQuantityValue',
    label: 'BOLT-QUANTITY *',
    help: boltConnectionTooltips.boltQuantity,
    required: true,
    focusTokens: ['BOLT-QUANTITY'],
  },
  {
    field: 'boltLengthValue',
    label: 'BOLT-LENGTH *',
    help: boltConnectionTooltips.boltLength,
    required: true,
    focusTokens: ['BOLT-LENGTH'],
  },
  {
    field: 'boltItemDescriptionValue',
    label: 'ITEM-DESCRIPTION *',
    help: boltConnectionTooltips.itemDescription,
    required: true,
    focusTokens: ['ITEM-DESCRIPTION'],
  },
  {
    field: 'boltMaterialValue',
    label: 'Material *',
    help: boltConnectionTooltips.material,
    required: true,
    focusTokens: ['MATERIAL'],
  },
  {
    field: 'boltStandardValue',
    label: 'Standard *',
    help: boltConnectionTooltips.standard,
    required: true,
    focusTokens: ['STANDARD'],
  },
];

export const SUPPORTING_FIELD_DEFINITIONS: readonly BoltFieldDefinition[] = [
  {
    field: 'boltBaseFamilyValue',
    label: 'BoltBaseFamily',
    help: boltConnectionTooltips.boltBaseFamily,
    required: false,
    readOnly: true,
    focusTokens: ['BOLTBASEFAMILY'],
  },
  {
    field: 'boltSetActiveValue',
    label: 'BoltSetActive *',
    help: boltConnectionTooltips.boltSetActive,
    required: true,
    focusTokens: ['BOLTSETACTIVE', 'BOLT-SET-ACTIVE'],
  },
];

export function resultToNotification(result: BoltConfigurationResult): Notification {
  if (result.success) {
    if (result.action === 'added_project_parameters') {
      return {
        open: true,
        severity: 'success',
        title: 'Bolt project parameters created',
        message: result.message,
      };
    }
    if (result.action === 'added_to_existing') {
      return {
        open: true,
        severity: 'success',
        title: `Bolt parameters added to ${result.targetObjectType} families`,
        message: result.message,
      };
    }
    if (result.action === 'loaded_new_family') {
      return {
        open: true,
        severity: 'success',
        title: `${result.targetObjectType} reference family loaded`,
        message: result.message,
      };
    }
    return {
      open: true,
      severity: 'info',
      title: 'Bolt parameters already available',
      message: result.message,
    };
  }
  return {
    open: true,
    severity: 'error',
    title: 'Bolt parameter setup could not be applied',
    message: result.error || result.message,
  };
}
